import {
    APIENV,
    getCustomiserState,
    useFacturationService,
    useSociete,
} from '@europrocurement/l2d-domain';
import {
    PanierPaiementApiApiPanierReglementPostRequest,
    PanierPaiementJsonldPanierCreate,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { SocieteJsonldSocieteReadItem } from '@europrocurement/l2d-domain/openApi/ApiSociete';
import { CollectionDeDossierJsonldCollectiondossierRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import useSocieteInfo from '../../Dossiers/hooks/useSocieteInfo';

const usePanierPay = () => {
    const { panierPaiementApi } = useFacturationService();
    const xIdSociete = useSociete();
    let searchCollectionTimeout: number | undefined;

    const l2dpAppUrl = () => APIENV.apps.l2dp[getCustomiserState().app.l2dp] || undefined;

    const { getSocieteData } = useSocieteInfo();

    const [societeData, setSocieteData] = useState<SocieteJsonldSocieteReadItem>();

    useEffect(() => {
        getSocieteData().then(setSocieteData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const creaPanierPay = (
        factureIds: Array<string>,
        collectionsIds: Array<string>,
    ): Promise<string> =>
        new Promise((resolve, reject) => {
            clearTimeout(searchCollectionTimeout);
            const panierPaiementJsonldPanierCreate: PanierPaiementJsonldPanierCreate = {
                factures: factureIds,
                collections: collectionsIds,
            };
            console.log(panierPaiementJsonldPanierCreate);
            const requestParameters: PanierPaiementApiApiPanierReglementPostRequest = {
                panierPaiementJsonldPanierCreate,
                xIdSociete,
            };
            searchCollectionTimeout = setTimeout(() => {
                panierPaiementApi
                    .apiPanierReglementPost(requestParameters)
                    .then((res) => {
                        if (res.data.uuid) {
                            resolve(`${l2dpAppUrl()}/panier/${res.data.uuid}`);
                            return;
                        }
                        reject(new Error('No uuid in response'));
                    })
                    .catch((err) => reject(err));
            }, 300);
        });

    const bodyMailCollection = async (
        contact: string,
        collection: CollectionDeDossierJsonldCollectiondossierRead,
    ): Promise<string> => {
        const panierUrl = await creaPanierPay([], [collection.id as unknown as string]);

        return `Bonjour ${contact},\n\nDans le cadre du traitement de la formalité ${collection?.numero}, nous vous informons qu’une provision est nécessaire.\n\n🔹 Montant de la provision : ${collection?.acompteAttendu} €\n🔹 Méthode de paiement : Virement bancaire ou paiement par carte bancaire\n\n📌 Action requise :\n✅ Si vous souhaitez régler par virement :\nMerci d’effectuer le paiement sur le compte ci-dessous et de nous transmettre votre preuve de règlement :\n\nIBAN : ${societeData?.compteBanque}\nBIC : ${societeData?.codeBic}\nRéférence à indiquer : ${collection?.numero}\n\n✅ Si vous souhaitez régler par carte bancaire :\nVous pouvez utiliser le lien de paiement sécurisé ci-dessous :\n${panierUrl}\n\n✅ Si un remboursement est prévu :\nMerci de bien vouloir nous communiquer un RIB en pièce jointe pour faciliter le remboursement éventuel.`;
    };
    const bodyMailFacture = async (
        contact: string,
        collection: CollectionDeDossierJsonldCollectiondossierRead,
        resteAPayer: string,
        invoiceToPayItems: number[],
    ): Promise<string> => {
        const panierUrl = await creaPanierPay(
            invoiceToPayItems.map((id) => id as unknown as string),
            [],
        );

        return `Bonjour ${contact},\n\nDans le cadre du traitement de la formalité ${collection?.numero}, nous vous informons qu'un paiement est nécessaire.\n\n🔹 Montant attendu : ${resteAPayer} €\n🔹 Méthode de paiement : Virement bancaire ou paiement par carte bancaire\n\n📌 Action requise :\n✅ Si vous souhaitez régler par virement :\nMerci d'effectuer le paiement sur le compte ci-dessous et de nous transmettre votre preuve de règlement :\n\nIBAN : ${societeData?.compteBanque}\nBIC : ${societeData?.codeBic}\nRéférence à indiquer : ${collection?.numero}\n\n✅ Si vous souhaitez régler par carte bancaire :\nVous pouvez utiliser le lien de paiement sécurisé ci-dessous :\n${panierUrl}\n\n✅ Si un remboursement est prévu :\nMerci de bien vouloir nous communiquer un RIB en pièce jointe pour faciliter le remboursement éventuel.`;
    };
    const navigateFacturePay = (factureIds: Array<string>): Promise<string | undefined> =>
        new Promise((resolve, reject) => {
            clearTimeout(searchCollectionTimeout);
            const panierPaiementJsonldPanierCreate: PanierPaiementJsonldPanierCreate = {
                factures: factureIds,
            };

            const requestParameters: PanierPaiementApiApiPanierReglementPostRequest = {
                panierPaiementJsonldPanierCreate,
                xIdSociete,
            };
            searchCollectionTimeout = setTimeout(() => {
                panierPaiementApi
                    .apiPanierReglementPost(requestParameters)
                    .then((res) => {
                        if (res.data.uuid) {
                            window.open(`${l2dpAppUrl()}/panier/${res.data.uuid}`, '_blank');
                        }
                    })
                    .catch((e) => {
                        console.error(e);

                        enqueueSnackbar('Paiement des factures sélectionnées impossible', {
                            variant: 'error',
                        });

                        return reject(e);
                    });
            }, 300);
        });

    const navigateCollectionPay = (collectionsIds: Array<string>): Promise<string | undefined> =>
        new Promise((resolve, reject) => {
            clearTimeout(searchCollectionTimeout);
            const panierPaiementJsonldPanierCreate: PanierPaiementJsonldPanierCreate = {
                collections: collectionsIds,
            };

            const requestParameters: PanierPaiementApiApiPanierReglementPostRequest = {
                panierPaiementJsonldPanierCreate,
                xIdSociete,
            };
            searchCollectionTimeout = setTimeout(() => {
                panierPaiementApi
                    .apiPanierReglementPost(requestParameters)
                    .then((res) => {
                        if (res.data.uuid) {
                            window.open(`${l2dpAppUrl()}/panier/${res.data.uuid}`, '_blank');
                        }
                    })
                    .catch((e) => {
                        console.error(e);

                        enqueueSnackbar('Paiement des commandes sélectionnées impossible', {
                            variant: 'error',
                        });

                        return reject(e);
                    });
            }, 300);
        });

    return {
        navigateFacturePay,
        navigateCollectionPay,
        l2dpAppUrl,
        creaPanierPay,
        bodyMailCollection,
        bodyMailFacture,
    };
};

export default usePanierPay;
